import { useCallback, useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react'
import { claimSafeTriangle, releaseSafeTriangle } from './hover-group'
import {
  getHeadingOrigin,
  getSafeTriangle,
  trackConeTrip,
  type Cone,
  type Point,
  type PointerSample,
} from './safe-triangle'

/** The only fields the cell's pointer handlers read, so a test can drive them without a real event. */
type CellPointer = Pick<PointerEvent<HTMLElement>, 'clientX' | 'clientY' | 'pointerType'>

/** Enough samples to span the heading window at any pointer rate in practice. */
const MAX_SAMPLES = 32

interface SafeTriangleOptions {
  id: string
  open: boolean
  triggerRef: RefObject<HTMLElement>
  popupRef: RefObject<HTMLElement>
  /** The pointer gave up on the card mid-trip. */
  onLeave: () => void
}

/**
 * Runs a cone trip each time the pointer leaves an open card's cell, holding
 * the hover group (see `requestHoverChange`) once the trip commits.
 *
 * Returns the cell's pointer handlers and the cone to bridge, since the overlay
 * is click-through there (see `nested/cone-bridge`).
 */
export function useSafeTriangle({ id, open, triggerRef, popupRef, onLeave }: SafeTriangleOptions) {
  const [bridgedCone, setBridgedCone] = useState<Cone | null>(null)
  const stopTripRef = useRef<(() => void) | null>(null)
  const samplesRef = useRef<Array<PointerSample>>([])

  /** `replayAt` only when the pointer gave up mid-trip: a row it crossed there may still want to open. */
  const endTrip = useCallback(
    (replayAt: Point | null) => {
      stopTripRef.current?.()
      stopTripRef.current = null
      releaseSafeTriangle(id, replayAt)
      setBridgedCone(null)
    },
    [id],
  )

  const handleTriggerPointerMove = useCallback((event: CellPointer) => {
    const samples = samplesRef.current
    samples.push({ x: event.clientX, y: event.clientY, at: performance.now() })
    if (samples.length > MAX_SAMPLES) samples.shift()
  }, [])

  const handleTriggerPointerLeave = useCallback(
    (event: CellPointer) => {
      endTrip(null)
      const exit = { x: event.clientX, y: event.clientY }
      const from = getHeadingOrigin(samplesRef.current, exit, performance.now())
      samplesRef.current = []
      const popup = popupRef.current
      const trigger = triggerRef.current
      if (!open || event.pointerType !== 'mouse' || !popup || !trigger) {
        return
      }
      const cone = getSafeTriangle(exit, popup.getBoundingClientRect())
      if (!cone) {
        return
      }
      stopTripRef.current = trackConeTrip({
        cone,
        exit,
        from,
        popup,
        trigger,
        onBridge: () => setBridgedCone(cone),
        onClaim: () => claimSafeTriangle(id),
        onEnd: (trip) => {
          if (trip.how !== 'abandoned') {
            endTrip(null)
            return
          }
          endTrip(trip.at)
          onLeave()
        },
      })
    },
    [id, open, popupRef, triggerRef, onLeave, endTrip],
  )

  // Ends the trip when the card closes some other way, and on unmount.
  useEffect(() => (open ? () => endTrip(null) : undefined), [open, endTrip])

  return {
    onTriggerPointerMove: handleTriggerPointerMove,
    onTriggerPointerLeave: handleTriggerPointerLeave,
    bridgedCone,
  }
}
